import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Injectable, Signal } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { format } from 'date-fns';
import { firstValueFrom, map, Observable, of } from 'rxjs';
import {
  BottomSheetAlertComponent,
  BottomSheetData,
} from 'src/app/components/bottom-sheet-alert/bottom-sheet-alert.component';
import { CustomSnackbarComponent } from 'src/app/components/custom-snackbar/custom-snackbar.component';
import {
  DialogAlert,
  DialogAlertComponent,
} from 'src/app/components/dialog-alert/dialog-alert.component';
import { SortBy } from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import { Colonna } from 'src/app/components/lista-tabellare/lista-tabellare.component';
import { environment } from 'src/environments/environment';

export type TipoElemento =
  | 'KPI'
  | 'RISPOSTETESTO'
  | 'IMPATTO'
  | 'IMPEGNO'
  | 'AZIONE'
  | 'CERTIFICAZIONE'
  | 'TABELLA'
  | 'NOTIFICA'
  | 'STAKEHOLDER'
  | 'UM'
  | 'DOMANDAINFORMATIVA'
  | 'SOTTORISPOSTA'
  | 'TEMATICA';

export type TipoModulo =
  | 'MATERIALITA'
  | 'SINTESI'
  | 'BILANCIO'
  | 'POLICY'
  | 'PIANO'
  | 'DOPPIA_MATERIALITA'
  | 'REPORT_VSME';

export const DATE_FORMATS = {
  SHORT: 'dd/MM/yyyy',
  MIDDLE: 'dd/MM/yyyy HH:mm',
  TIME: 'HH:mm',
};

export const TIPO_MODULO: TipoModulo[] = [
  'MATERIALITA',
  'SINTESI',
  'BILANCIO',
  'POLICY',
  'PIANO',
  'DOPPIA_MATERIALITA',
  'REPORT_VSME',
];

export const TIPO_MODULO_LABEL: Record<TipoModulo, string> = {
  MATERIALITA: 'Materialità',
  SINTESI: 'Sintesi',
  BILANCIO: 'Report',
  POLICY: 'Policy',
  PIANO: 'Piano',
  DOPPIA_MATERIALITA: 'Doppia Materialità',
  REPORT_VSME: 'Report VSME',
};

@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  /*   private DATE_FORMATS = {
      SHORT: 'd.MMM yyyy',
      MIDDLE: 'd.MMM yyyy, E HH:mm, ',
      TIME: 'HH:mm',
    }; */

  constructor(
    private sanitizer: DomSanitizer,
    public dialog: MatDialog,
    readonly snackBar: MatSnackBar,
    private readonly _bottomSheet: MatBottomSheet,
  ) {}

  //************************************* GESTIONE ID  */
  /**
   * Genera un id compatibile con ObjectId di MongoDB (24 caratteri esadecimali):
   * 4 byte di timestamp (secondi) + 8 byte casuali
   * @returns id esadecimale di 24 caratteri
   */
  public generaObjectId(): string {
    const timestamp = Math.floor(Date.now() / 1000)
      .toString(16)
      .padStart(8, '0');
    const random = Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) =>
      byte.toString(16).padStart(2, '0'),
    ).join('');
    return timestamp + random;
  }

  //************************************* GESTIONE ALERT  */
  /**
   * Dialog per gestire gli alert
   * @param config
   * @param maxWidth
   * @param maxHeight
   * @returns
   */
  openDialog(
    config: DialogAlert,
    maxWidth?: number | string,
    maxHeight?: number | string,
  ): MatDialogRef<DialogAlertComponent> {
    return this.dialog.open(DialogAlertComponent, {
      data: config,
      disableClose: true,
      maxWidth,
      maxHeight,
      autoFocus: false,
    });
  }

  /**
   * Chiudo tutte le dialog
   */
  public closeDialog() {
    this.dialog.closeAll();
  }

  //************************************* GESTIONE BOTTOMSHEET *******  */

  /**
   *
   * @param config
   * @returns
   */
  openBottomSheet(config: BottomSheetData): MatBottomSheetRef<BottomSheetAlertComponent> {
    return this._bottomSheet.open(BottomSheetAlertComponent, {
      data: config,
    });
  }

  /******************************************** END GESTIONE ALERT  */

  /**
   * Sanificatore di html
   * @param html
   * @returns
   */
  public sanitizeHtml(html: string) {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /************************************** GESTIONE TOAST SNACK  */

  /**
   * Gestione del toast
   * @param message
   * @param action bottone sul toast (Gestito solo con la chiususura per adesso)
   * @param config
   * @returns
   */
  public openSnackBar(message: string, action = '', config?: MatSnackBarConfig) {
    const snackBarRef = this.snackBar.open(message, action, config);

    snackBarRef.onAction().subscribe(() => {
      snackBarRef.dismiss();
    });

    return snackBarRef;
  }

  public formatDateInString(date: Date, formatDate: 'SHORT' | 'MIDDLE' | 'TIME') {
    const dtf = DATE_FORMATS[formatDate];
    return format(date, dtf);
  }

  public getDatiTableArray(
    arrayDati: any[],
    searchKey: string[],
    ricerca?: string,
    page?: number,
    pageSize?: number,
    sortBy?: SortBy[],
  ): Observable<any> {
    if (!page) {
      page = 0;
    }
    if (!pageSize) {
      pageSize = 5;
    }

    let array: any[] = [];

    if (ricerca && searchKey.length) {
      array = arrayDati.filter((item) => {
        let check = false;

        const filtro = ricerca?.trim().toLowerCase();
        if (!filtro) return true;

        for (const chiave of searchKey) {
          if (item[chiave]?.toLowerCase().includes(filtro)) {
            check = true;
            break;
          }
        }

        return check;
      });
    } else {
      array = arrayDati;
    }

    if (sortBy?.length) {
      array.sort((record1, record2) => {
        for (let i = 0; i < sortBy.length; i++) {
          const chiave = sortBy[i].chiave;
          const direction = sortBy[i].sort === 'asc' ? 1 : -1;
          let esito = 0;

          let val1: number | string = record1[chiave];
          let val2: number | string = record2[chiave];

          if (typeof val1 !== 'number') {
            val1 = val1.toString().toLowerCase();
          }

          if (typeof val2 !== 'number') {
            val2 = val2.toString().toLowerCase();
          }

          if (val1 && !val2) {
            esito = -1;
          } else if (!val1 && val2) {
            esito = 1;
          } else if (val1 < val2) {
            esito = -1;
          } else if (val1 > val2) {
            esito = 1;
          }

          if (esito !== 0) {
            return esito * direction;
          }
        }

        return 0;
      });
    }

    return of({
      content: array.slice(page * pageSize, (page + 1) * pageSize),
      totalElements: array.length,
    });
  }

  public static emailValidator(control: any) {
    // Regular expression for a valid email address
    const EMAIL_REGEXP = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return EMAIL_REGEXP.test(control.value)
      ? null
      : {
          validateEmail: {
            valid: false,
          },
        };
  }

  public getBaseUrl() {
    if (location.href.startsWith(environment.urlGo)) {
      return environment.urlGo;
    } else {
      return environment.url;
    }
  }

  public static blankValidator(control: any) {
    const value = control?.value;
    const isWhitespace = typeof value === 'string' && value.trim().length === 0;
    const isValid = !isWhitespace;
    return isValid
      ? null
      : {
          blankValidator: {
            valid: false,
          },
        };
  }

  public static blankValidatorStakeholders(control: any) {
    const value = control?.value;

    // Controlla se il valore contiene solo spazi (uno o più)
    const isOnlyWhitespace =
      typeof value === 'string' && value.trim().length === 0 && value.length > 0;
    const isValid = !isOnlyWhitespace;

    return isValid
      ? null
      : {
          blankValidator: {
            valid: false,
          },
        };
  }

  convertGigabytesToBytes(gigabytes: number | undefined): number {
    if (gigabytes) {
      const bytesInOneGigabyte = 1024 ** 3; // 1 GB = 1024^3 bytes
      return parseFloat((gigabytes * bytesInOneGigabyte).toFixed(1));
    } else {
      return 0;
    }
  }

  convertBytesToGigabytes(bytes: number | undefined): number {
    if (bytes) {
      const bytesInOneGigabyte = 1024 ** 3; // 1 GB = 1024^3 bytes
      const risultato = parseFloat((bytes / bytesInOneGigabyte).toFixed(1));
      return risultato;
    } else {
      return 0;
    }
  }

  /**
   * Metodo per gestire il componente chip in filtri, poichè ha una struttura ben definita
   * @param ricerca
   * @param elementiPreSelezionati elementi preselezionati precedentemente all applicazione dei filtri
   * @returns
   */
  _getTagsPerFiltro(
    ricerca: string | undefined,
    elementiPreSelezionati: string[] | undefined,
    metodoService: any,
  ) {
    return metodoService.pipe(
      map((tags: string[]) => {
        if (!ricerca) {
          let tagFiltrati = tags;

          if (elementiPreSelezionati) {
            tagFiltrati = tagFiltrati.filter((tag) => elementiPreSelezionati.includes(tag));
          }

          return tagFiltrati;
        }
        return tags.filter((item) => item.toLowerCase().includes(ricerca.toLowerCase()));
      }),
    );
  }

  /**
   * Metodo generale che mi fa il drag and drop e sorting su liste filtrate e non.
   * @param event evento del drag & drop su quale azionare
   * @param arrayElementiSel
   * @param arrayElementiSelFiltrati
   * @returns oggetto con gli array calcolati
   */
  dropElementi(
    event: CdkDragDrop<any[]>,
    arrayElementiSel: any[],
    arrayElementiSelFiltrati: any[],
  ) {
    // Calcolo gli elementi nascosti dal filtro
    const elementiNascostiDalFiltro = arrayElementiSel.filter((elem1) => {
      return !arrayElementiSelFiltrati.some((elem2) => elem2.id === elem1.id);
    });

    // Funzione per ottenere indice non filtrato
    const getIndexNonFiltrato = (id: string) =>
      arrayElementiSel.findIndex((elem) => elem?.id === id);

    // Funzione per applicare il filtro iniziale
    const ripristinaFiltro = () => {
      arrayElementiSelFiltrati = arrayElementiSelFiltrati.filter((elem2) => {
        return !elementiNascostiDalFiltro.some((elem1) => elem1.id === elem2.id);
      });
    };

    const isSorting = event.previousContainer === event.container;
    const isAzioneSuSelezionati = event.container.data.every((item1) =>
      arrayElementiSel.some((item2) => item2.id === item1.id),
    );

    // Caso: Comportamento con filtro
    if (elementiNascostiDalFiltro.length) {
      if (isSorting) {
        // Sorting nella lista filtrata

        if (isAzioneSuSelezionati) {
          /*  Mi calcolo Index elemeno sulla lista non filtrata
             Perchè se ho filtrato vuoldire che gli indici in visualizzazione
             non corrispondono più con quelli originali per fare il sorting */
          const idForIdx = arrayElementiSelFiltrati[event.previousIndex]?.id;
          const findIdx = getIndexNonFiltrato(idForIdx);

          moveItemInArray(arrayElementiSel, findIdx, event.currentIndex);
          moveItemInArray(arrayElementiSelFiltrati, event.previousIndex, event.currentIndex);
        } else {
          moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        }
      } else {
        // Sono in lista filtrata e faccio drag and drop

        if (isAzioneSuSelezionati) {
          // Drag and drop da DISPONIBILI a SELEZIONATI

          /* Prendo l'id dell elemento sopra a quello spostato, Per avere un riferimento di posizione 
             Questo perchè quando sposto un elemento lo posso rilasciare in qualsisi posizione, quindi 
            prendento un indice di riferimento, quando rimuovo i filtri, l'elemento spostato andra sotto quel
            indice di riferimento */
          const idElemSopra = arrayElementiSelFiltrati[event.currentIndex - 1]?.id;

          /* Tovo l'indice corretto per la lista non filtrata. Ovvero se nella lista filtratra ho spostato
           un elemento sotto [id : 1] allora mi prendo l'id del elemento sopra [id 1 - 1] e cerco l'id
           trovato l'id del elemento nella lista non fitlrata mi prendo index di spostamento sommando al indice trovato + 1 */
          const findIdx = getIndexNonFiltrato(idElemSopra) + 1;

          // Mi inserisco l'elemento nella posizione corretta nella lista non filtrata
          transferArrayItem(
            event.previousContainer.data,
            event.container.data,
            event.previousIndex,
            findIdx,
          );

          // Mi inserisco l'elemento anche nella lista filtrata, sul indice selezionato dall'interfaccia perhcè corrisponde.
          arrayElementiSelFiltrati.splice(event.currentIndex, 0, arrayElementiSel[findIdx]);

          // Rimuovo gli elementi nascosti per ritornare al filtro di prima
          ripristinaFiltro();
        } else {
          // Drag and drop da SELEZIONATI a DISPONIBILI

          // Mi trovo l'indice della lista non filtrata, questo perchè event.previousIndex mi risulta l'indice della lista filtrata
          const idxListaNonFiltrata = getIndexNonFiltrato(
            arrayElementiSelFiltrati[event.previousIndex].id,
          );

          //Rimuovo l'elemento che ho spostato dalla lista filtrata
          arrayElementiSelFiltrati = arrayElementiSelFiltrati.filter(
            (item) => item.id !== event.previousContainer.data[idxListaNonFiltrata].id,
          );

          // Trasferisco l'elemento con l'indice corretto
          transferArrayItem(
            event.previousContainer.data,
            event.container.data,
            idxListaNonFiltrata,
            event.currentIndex,
          );
        }
      }
    } else {
      // Caso: Comportamento regolare senza filtro
      arrayElementiSelFiltrati = arrayElementiSel; // stesso riferimento di memoria

      if (isSorting) {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      } else {
        transferArrayItem(
          event.previousContainer.data,
          event.container.data,
          event.previousIndex,
          event.currentIndex,
        );
      }
    }

    const obj = {
      arrayElementiSelFiltrati: arrayElementiSelFiltrati,
      arrayElementiSel: arrayElementiSel,
    };

    return obj;
  }

  /**
   * Metodo che mi crea la colonna stato per le tabelle
   * @returns
   */
  getColonnaStato() {
    return {
      title: 'Stato',
      sortable: true,
      campiSort: ['stato'],
      value: (record: any) => {
        if (record?.stato === 'PUBBLICATO') {
          return '<div style="display: flex;justify-content: center;align-items:center;background-color:#CCDBCD;width:130px;text-align: center;border-radius: 15px;height:27px"><p style="color:#729373;font-size:13px">Pubblicata</p></div>';
        } else if (record?.stato === 'BOZZA') {
          return '<div style="display: flex;justify-content: center;align-items:center;background-color:#FFE199;width:130px;text-align: center;border-radius: 15px;height:27px"><p style="color:#C7A045;font-size:13px">Bozza</p></div>';
        } else if (record?.stato === 'ARCHIVIATO') {
          return '<div style="display: flex;justify-content: center;align-items:center;background-color:#c53032;width:130px;text-align: center;border-radius: 15px;height:27px"><p style="color:#fff;font-size:13px">Archiviato</p></div>';
        } else {
          return '';
        }
      },
    };
  }

  /**
   * Restituisce una configurazione generica per una colonna di tipo "button".
   *
   * La configurazione consente di definire il comportamento del pulsante in base allo stato del record:
   * - BOZZA → usa i valori definiti in `config.bozza`
   * - PUBBLICATO → usa i valori definiti in `config.pubblicato`
   * - ARCHIVIATO → usa i valori definiti in `config.archiviato`
   *
   * @param config Oggetto di configurazione che definisce icona, titolo e id del pulsante per ciascuno stato.
   * @returns Un oggetto conforme all’interfaccia `Colonna` con type `'button'`.
   */
  getButtonColumnConfig(config: {
    bozza: { icon: string; title: string; id: string };
    pubblicato: { icon: string; title: string; id: string };
    archiviato: { icon: string; title: string; id: string };
  }): Colonna {
    return {
      type: 'button',
      buttonIcon: (record: any) => {
        switch (record?.stato) {
          case 'BOZZA':
            return config.bozza.icon;
          case 'PUBBLICATO':
            return config.pubblicato.icon;
          case 'ARCHIVIATO':
            return config.archiviato.icon;
          default:
            return '';
        }
      },
      title: (record: any) => {
        switch (record?.stato) {
          case 'BOZZA':
            return config.bozza.title;
          case 'PUBBLICATO':
            return config.pubblicato.title;
          case 'ARCHIVIATO':
            return config.archiviato.title;
          default:
            return '';
        }
      },
      buttonId: (record: any) => {
        switch (record?.stato) {
          case 'BOZZA':
            return config.bozza.id;
          case 'PUBBLICATO':
            return config.pubblicato.id;
          case 'ARCHIVIATO':
            return config.archiviato.id;
          default:
            return '';
        }
      },
      nascondiButton: false,
    };
  }

  /**
   * Restituisce la configurazione della colonna "Elimina/Archivia/Ripristina".
   *
   * - Se `record.stato` è **BOZZA** → mostra pulsante "Elimina" con icona `delete`
   * - Se `record.stato` è **PUBBLICATO** → mostra pulsante "Archivia" con icona `inventory_2`
   * - Se `record.stato` è **ARCHIVIATO** → mostra pulsante "Ripristina" con icona `unarchive`
   *
   * @returns Oggetto `Colonna` configurato per il pulsante di eliminazione/archiviazione.
   */
  getColonnaElimina(): Colonna {
    return this.getButtonColumnConfig({
      bozza: { icon: 'delete', title: 'Elimina', id: 'elimina' },
      pubblicato: { icon: 'inventory_2', title: 'Archivia', id: 'archivia' },
      archiviato: { icon: 'unarchive', title: 'Ripristina', id: 'disarchivia' },
    });
  }

  /**
   * Mostra un dialog di conferma per eliminare, archiviare o disarchiviare un modulo e
   * esegue l'operazione corrispondente sul service passato.
   *
   * @async
   * @param {any} record - L'oggetto del modulo su cui eseguire l'operazione. Deve contenere almeno `id` e `stato`.
   * @param {'BILANCIO' | 'SINTESI' | 'POLICY' | 'PIANO'} tipoModulo - Il tipo di modulo da gestire.
   * @param {{ delete: Function; archivia: Function; disarchivia: Function }} service - Il service da utilizzare per l'operazione. Deve implementare i metodi:
   *   - `delete(id: any)`: elimina il modulo in stato BOZZA.
   *   - `archivia(id: any)`: archivia il modulo in stato PUBBLICATO.
   *   - `disarchivia(id: any, settoreId: any)`: disarchivia il modulo in stato ARCHIVIATO.
   *
   * @returns {Promise<boolean>} Ritorna una Promise che si risolve con:
   *   - `true` se l'operazione è stata confermata e completata con successo.
   *   - `false` se l'operazione è stata annullata o se si è verificato un errore.
   *
   * @example
   *  Uso nel componente con async/await
   * const success = await utilityService.eliminaModulo(
   *   bilancio,
   *   'BILANCIO',
   *   bilancioService
   * );
   * if (success) {
   *   tabella.caricaDati();
   *   utilityService.mostraMessaggio('Eliminazione completata');
   * }
   */
  async eliminaModulo(
    record: any,
    service: {
      delete: (id: string) => Observable<any>;
      archivia: (id: string) => Observable<any>;
      disarchivia: (id: string, settoreId: string) => Observable<any>;
    },
    deleteByDeveloper: boolean,
  ): Promise<boolean> {
    let descrizioneElimina = '';
    let titolo = '';
    let operazione$: any;

    // Determino quale operazione deve essere eseguita
    const descrizioneModulo = this.getDescrizioneTitoloDialogEliminazione(record);

    const deleteIrreversibile = () => {
      titolo = 'Eliminazione Dati Irreversibile';
      descrizioneElimina = `
        ${descrizioneModulo}
        <div>
          <p><strong>Si prega di notare che questa operazione eliminerà anche tutte le compilazioni raccolte associate.</strong></p>
          <ul>
            <li>L’eliminazione è definitiva e <strong>NON può essere annullata</strong>.</li>
            <li>Tutte le informazioni e le compilazioni raccolte saranno perse <strong>permanentemente</strong>.</li>
          </ul>
          <p><strong>Sei sicuro di voler continuare?</strong></p>
        </div>`;
      operazione$ = service.delete(record?.id);
    };

    if (deleteByDeveloper) {
      deleteIrreversibile();
    } else {
      switch (record?.stato) {
        case 'BOZZA':
          deleteIrreversibile();
          break;

        case 'PUBBLICATO':
          titolo = `Archiviazione`;

          descrizioneElimina = `Confermi l'archiviazione del seguente modulo?<br>${descrizioneModulo}`;

          operazione$ = service.archivia?.(record?.id);
          break;

        case 'ARCHIVIATO':
          titolo = `Ripristino`;
          descrizioneElimina = `Confermi il ripristino del seguente modulo?<br>${descrizioneModulo}`;
          operazione$ = service.disarchivia?.(record?.id, record.settori?.[0]?.id);
          break;

        default:
          console.warn('Stato non gestito:', record?.stato);
          return false;
      }
    }

    // Restituisce un Promise<boolean> che si risolve alla chiusura del dialog
    return new Promise<boolean>((resolve) => {
      this.openDialog({
        titolo,
        descrizione: descrizioneElimina,
        fontWeight: '500',
        bottoni: [
          {
            nome_btn: 'NO',
            background_color: 'var(--mat-color-accent)',
            handler: () => resolve(false),
          },
          {
            nome_btn: 'SI',
            background_color: 'var(--mat-color-primary)',
            handler: async () => {
              try {
                await firstValueFrom(operazione$);
                resolve(true);
              } catch (err: any) {
                this.openDialog({
                  titolo: 'Attenzione',
                  descrizione:
                    'Impossibile completare l’operazione: ' +
                    (err?.error?.message || 'Errore sconosciuto'),
                  bottoni: [{ nome_btn: 'Chiudi' }],
                });
                resolve(false);
              }
            },
          },
        ],
      });
    });
  }

  getDescrizioneTitoloDialogEliminazione(modulo: any): string {
    const titolo = modulo?.nomeContesto || 'N/D';
    const anno = modulo?.anno || 'N/D';
    const settore = modulo?.settori?.[0]?.titolo || 'N/D';

    return `
    <ul style="list-style:none; padding-left:0;">
      <li><strong>Modulo:</strong> ${titolo}</li>
      <li><strong>Settore:</strong> ${settore}</li>
      <li><strong>Anno:</strong> ${anno}</li>
    </ul>
  `;
  }

  /**
   * Metodo per convertire un file base64 in excel
   * @param base64Data data file
   * @param fileName nome file
   */
  downloadExcel(base64Data: string, fileName?: string): void {
    const cleanedBase64 = base64Data.split(',')[1] || base64Data;
    fetch(
      `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${cleanedBase64}`,
    )
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName ?? '';
        a.click();
        URL.revokeObjectURL(url);
      });
  }

  /**
   * Snackbar live, ovvero che rimane aperto e posso cambiare la scritta all'interno (il messagio)
   * @param messageSignal di tipo Signal per avere una variazione in tempo reale sul messaggio
   * @param panelClass stile del panel
   * @returns
   */
  // utility.service.ts
  openLiveSnackBar(messageSignal: Signal<string>, panelClass: string = 'info-snackbar') {
    return this.snackBar.openFromComponent(CustomSnackbarComponent, {
      data: { message: messageSignal },
      panelClass: [panelClass],
    });
  }
}
